"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
interface DropdownContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
}
const DropdownContext = React.createContext<DropdownContextValue | null>(null);

/**
 * Dropdown — shadcn-style primitive sin dependencia de @radix-ui/react-dropdown-menu.
 * Usa radix-slot para asChild y un focus-trap simple para a11y.
 * Soporta: click outside, Escape, Arrow nav, focus return, portal.
 */
export function Dropdown(t0) {
  const $ = _c(9);
  const {
    open,
    onOpenChange,
    children,
    defaultOpen: t1
  } = t0;
  const defaultOpen = t1 === undefined ? false : t1;
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = open !== undefined;
  const actualOpen = isControlled ? open as boolean : internalOpen;
  let t2;
  if ($[0] !== isControlled || $[1] !== onOpenChange) {
    t2 = v => {
      if (!isControlled) {
        setInternalOpen(v);
      }
      onOpenChange?.(v);
    };
    $[0] = isControlled;
    $[1] = onOpenChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setOpen = t2;
  const triggerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  let t3;
  if ($[3] !== actualOpen || $[4] !== setOpen) {
    t3 = {
      open: actualOpen,
      setOpen,
      triggerRef,
      contentRef
    };
    $[3] = actualOpen;
    $[4] = setOpen;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const value = t3;
  let t4;
  if ($[6] !== children || $[7] !== value) {
    t4 = <DropdownContext.Provider value={value}>{children}</DropdownContext.Provider>;
    $[6] = children;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export function DropdownTrigger({
  asChild,
  children,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
}) {
  const ctx = React.useContext(DropdownContext);
  if (!ctx) throw new Error("DropdownTrigger must be inside Dropdown");
  const Comp = asChild ? Slot : "button";
  return <Comp
  // eslint-disable-next-line react-hooks/refs -- triggerRef es un ref estable del contexto (nunca se lee .current en render); pasarlo como ref al trigger es el patrón documentado de Radix Slot, no una lectura durante el render
  ref={ctx.triggerRef} aria-haspopup="menu"
  // eslint-disable-next-line react-hooks/refs
  aria-expanded={ctx.open} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement> & React.MouseEvent<HTMLButtonElement>);
    if (!e.defaultPrevented) ctx.setOpen(!ctx.open);
  }} {...props}>
      {children}
    </Comp>;
}
export interface DropdownContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "start" | "end" | "center";
  sideOffset?: number;
}
export function DropdownContent(t0) {
  const $ = _c(44);
  let children;
  let className;
  let props;
  let style;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      children,
      align: t1,
      sideOffset: t2,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const align = t1 === undefined ? "end" : t1;
  const sideOffset = t2 === undefined ? 6 : t2;
  const ctx = React.useContext(DropdownContext);
  if (!ctx) {
    throw new Error("DropdownContent must be inside Dropdown");
  }
  const {
    open,
    setOpen,
    triggerRef,
    contentRef
  } = ctx;
  const [coords, setCoords] = React.useState(null);
  let t3;
  if ($[7] !== align || $[8] !== contentRef || $[9] !== sideOffset || $[10] !== triggerRef) {
    t3 = () => {
      const el = triggerRef.current;
      if (!el) {
        return;
      }
      const r = el.getBoundingClientRect();
      const menuW = contentRef.current?.offsetWidth || 200;
      const menuH = contentRef.current?.offsetHeight || 240;
      const vw = window.innerWidth;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      let left = r.left;
      if (align === "end") {
        left = r.right - menuW;
      }
      if (align === "center") {
        left = r.left + r.width / 2 - menuW / 2;
      }
      left = Math.max(8, Math.min(left, vw - menuW - 8));
      let top = r.bottom + sideOffset;
      if (top + menuH > vh - 8 && r.top - sideOffset - menuH > 8) {
        top = r.top - sideOffset - menuH;
      } else {
        top = Math.min(top, Math.max(8, vh - menuH - 8));
      }
      setCoords({
        top,
        left
      });
    };
    $[7] = align;
    $[8] = contentRef;
    $[9] = sideOffset;
    $[10] = triggerRef;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const updateCoords = t3;
  const {
    shouldRender,
    isLeaving
  } = useAnimatedOpen(open, 150);
  let t4;
  let t5;
  if ($[12] !== open || $[13] !== updateCoords) {
    t4 = () => {
      if (!open) {
        return;
      }
      updateCoords();
      const onResize = () => updateCoords();
      window.addEventListener("resize", onResize);
      window.addEventListener("scroll", onResize, true);
      return () => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("scroll", onResize, true);
      };
    };
    t5 = [open, updateCoords];
    $[12] = open;
    $[13] = updateCoords;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[16] !== contentRef || $[17] !== open || $[18] !== setOpen || $[19] !== triggerRef) {
    t6 = () => {
      if (!open) {
        return;
      }
      const el_0 = contentRef.current;
      if (!el_0) {
        return;
      }
      const first = el_0.querySelector("[role=\"menuitem\"], a, button:not([disabled])");
      requestAnimationFrame(() => first?.focus());
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          e.stopPropagation();
          setOpen(false);
          (triggerRef.current as HTMLElement | null)?.focus();
          return;
        }
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          const items = Array.from(el_0.querySelectorAll("[role=\"menuitem\"]:not([aria-disabled=\"true\"]), a[href], button:not([disabled])"));
          if (items.length === 0) {
            return;
          }
          const idx = items.indexOf(document.activeElement as HTMLElement);
          let next;
          if (e.key === "ArrowDown") {
            next = idx === -1 ? 0 : (idx + 1) % items.length;
          } else {
            next = idx <= 0 ? items.length - 1 : idx - 1;
          }
          items[next]?.focus();
        }
        if (e.key === "Tab") {
          setOpen(false);
        }
      };
      const handleClickOutside = e_0 => {
        const target = e_0.target as Node;
        const el_1 = target as HTMLElement | null;
        if (el_1?.closest?.("[data-testid=\"language-selector-menu\"]")) {
          return;
        }
        if (el_1?.closest?.("[data-testid=\"theme-selector-menu\"]")) {
          return;
        }
        if (el_1?.closest?.("[data-testid=\"language-selector\"]") && contentRef.current?.querySelector("[data-testid=\"language-selector\"]")) {
          return;
        }
        const inTrigger = triggerRef.current?.contains(target);
        const inContent = contentRef.current?.contains(target);
        if (!inTrigger && !inContent) {
          setOpen(false);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    };
    $[16] = contentRef;
    $[17] = open;
    $[18] = setOpen;
    $[19] = triggerRef;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== contentRef || $[22] !== open || $[23] !== setOpen || $[24] !== shouldRender || $[25] !== triggerRef) {
    t7 = [open, setOpen, triggerRef, contentRef, shouldRender];
    $[21] = contentRef;
    $[22] = open;
    $[23] = setOpen;
    $[24] = shouldRender;
    $[25] = triggerRef;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  React.useEffect(t6, t7);
  const prevOpen = React.useRef(open);
  let t8;
  let t9;
  if ($[27] !== open || $[28] !== triggerRef) {
    t8 = () => {
      if (prevOpen.current && !open) {
        requestAnimationFrame(() => (triggerRef.current as HTMLElement | null)?.focus());
      }
      prevOpen.current = open;
    };
    t9 = [open, triggerRef];
    $[27] = open;
    $[28] = triggerRef;
    $[29] = t8;
    $[30] = t9;
  } else {
    t8 = $[29];
    t9 = $[30];
  }
  React.useEffect(t8, t9);
  if (!shouldRender) {
    return null;
  }
  if (typeof document === "undefined") {
    return null;
  }
  const t10 = coords?.top ?? 0;
  const t11 = coords?.left ?? 0;
  let t12;
  if ($[31] !== style || $[32] !== t10 || $[33] !== t11) {
    t12 = {
      position: "fixed",
      top: t10,
      left: t11,
      ...style
    };
    $[31] = style;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const t13 = isLeaving ? "animate-out fade-out slide-out-to-top-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none";
  let t14;
  if ($[35] !== className || $[36] !== t13) {
    t14 = cn("z-[9999] min-w-[160px] max-w-[calc(100vw-16px)] max-h-[70dvh] overflow-x-hidden overflow-y-auto overscroll-contain break-words rounded-[var(--radius-hero-menu,16px)] border border-[var(--border-strong)] surface-blur p-1 shadow-[var(--shadow-panel)]", t13, className);
    $[35] = className;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== children || $[39] !== contentRef || $[40] !== props || $[41] !== t12 || $[42] !== t14) {
    t15 = createPortal(<div ref={contentRef} role="menu" aria-orientation="vertical" data-dropdown-content={true} style={t12} className={t14} {...props}>{children}</div>, document.body);
    $[38] = children;
    $[39] = contentRef;
    $[40] = props;
    $[41] = t12;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}
export function DropdownItem(t0) {
  const $ = _c(15);
  let children;
  let className;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClick;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    onClick = $[3];
    props = $[4];
  }
  const ctx = React.useContext(DropdownContext);
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex w-full items-center rounded-[var(--radius-sm)] px-3 py-2 text-sm text-muted-foreground outline-none transition-colors hover:bg-[var(--muted)] hover:text-foreground focus:bg-[var(--muted)] focus:text-foreground focus-visible:ring-2 focus-visible:ring-[var(--accent)]", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== ctx || $[8] !== onClick) {
    t2 = e => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        ctx?.setOpen(false);
      }
    };
    $[7] = ctx;
    $[8] = onClick;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== props || $[12] !== t1 || $[13] !== t2) {
    t3 = <button type="button" role="menuitem" tabIndex={-1} className={t1} onClick={t2} {...props}>{children}</button>;
    $[10] = children;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
export function DropdownSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("my-1 h-px bg-[var(--border)]", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} role="separator" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DropdownLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-3 py-1.5 text-xs font-medium text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}